import type { FC } from "react";
import React, { useEffect, useRef } from "react";
import {
  useRouter,
  useTracking,
  useTranslation,
} from "@edreamsodigeo/frontend-core";
import { Body, Box, Flex, Glyph } from "@edreamsodigeo/prisma-design-system";

import { LightboxPill } from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxPill";
import type { AccordionItemProps } from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxBenefits/types";
import styles from "./styles";
import { Plan } from "~/clientState/hooks/model/lighboxContract";
import BenefitsWithTermsAndConditions from "../../components/BenefitsWithTermsAndConditions";
import {
  ActionsEnum,
  getCategoryLabel,
  trackingLabels,
} from "../../../../config/tracking";
import { usePrimeLightboxAccordion } from "~/clientState/providers/PrimeLightboxAccordionProvider";

const AccordionItemMobile: FC<AccordionItemProps> = ({
  item,
  openIndex,
  handleItemClick,
}) => {
  const { t } = useTranslation();
  const { pathname } = useRouter();
  const { trackEvent } = useTracking();
  const accordionItemMobileRef = useRef<HTMLDivElement>(null);
  const {
    accordionToOpen: accordionToOpenMobile,
    setAccordionToOpen: setAccordionToOpenMobile,
  } = usePrimeLightboxAccordion();
  const isOpen = openIndex === item.key || accordionToOpenMobile === item.key;

  useEffect(() => {
    if (accordionToOpenMobile === item.key && accordionItemMobileRef) {
      setTimeout(() => {
        accordionItemMobileRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 1500);
      trackEvent({
        category: getCategoryLabel(pathname),
        action: ActionsEnum.PRIME_FIRST_BENEFITS,
        label: trackingLabels.onLoadWithBenefitOpened({
          pathname: pathname,
          key: item.key,
        }),
      });
    }
  }, [accordionToOpenMobile, item.key, pathname, trackEvent]);

  const handleItemClickWithTrackingMobile = (key: string): void => {
    setAccordionToOpenMobile(undefined);
    handleItemClick(key);
    trackEvent({
      category: getCategoryLabel(pathname),
      action: ActionsEnum.PRIME_LIGHTBOX,
      label: trackingLabels.onClickExpandBenefit({
        pathname: pathname,
        key: key,
        isOpen: isOpen,
      }),
    });
  };

  return (
    <Box
      sx={styles.container}
      id={"mobile_accordion_item_" + item.key}
      ref={accordionItemMobileRef}
    >
      <Flex flex={"row"} gap={"1"} sx={styles.promotionalPill}>
        {item.appOnly && (
          <LightboxPill type={"promotional"} rounded>
            {t("visibility.lightbox.accordion.app.pill.text")}
          </LightboxPill>
        )}
        {item.plan === Plan.PrimePlus && (
          <LightboxPill type={"promotional"} rounded>
            {t("visibility.lightbox.accordion.all.pill.text")}
          </LightboxPill>
        )}
      </Flex>
      <Flex
        sx={styles.accordionItem}
        style={{
          alignItems: isOpen ? "normal" : "center",
          transition: isOpen
            ? "alignItems 300ms ease-out"
            : "alignItems 150ms ease-out",
          boxShadow: isOpen ? "0 4px 24px 0 #0000001A" : "none",
        }}
        onClick={() => handleItemClickWithTrackingMobile(item.key)}
        data-testid={`handleItemClick-${item.title}`}
      >
        <Box sx={styles.iconContainer}>
          <Glyph
            color={"primary.50"}
            size={"large"}
            category={item.iconCategory}
            name={item.icon}
          />
        </Box>
        <Box sx={styles.content}>
          <Body
            type={"small"}
            weight={"medium"}
            color={"neutral.90"}
            sx={styles.title}
          >
            {t(item.title)}
          </Body>
          <BenefitsWithTermsAndConditions
            description={t(item.description)}
            termsAndConditions={
              item.termsAndConditions ? t(item.termsAndConditions) : ""
            }
            termsAndConditionsLink={
              item.termsAndConditionsLink ? t(item.termsAndConditionsLink) : ""
            }
            isOpen={isOpen}
          />
        </Box>
        <Box sx={styles.uiAction}>
          <Glyph
            category={"UIActions"}
            color={"primary.50"}
            name={isOpen ? "Clear" : "Plus"}
            sx={styles.uiActionIcon}
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default AccordionItemMobile;
